<template>
  <section>
    <h2>人工編碼</h2>

    <b-button
      variant="outline-secondary"
      class="mb-3"
      :disabled="isLoadingCriterion || isImporting || isSavingResult"
      @click="loadCriteria"
    >
      <b-icon icon="arrow-clockwise" aria-hidden="true" />
      更新準則列表
    </b-button>

    <b-form-group label="選擇準則庫" label-for="qa-human-criterion">
      <b-form-select
        id="qa-human-criterion"
        v-model="selectedCriterionId"
        :disabled="isLoadingCriterion || isImporting || isSavingResult"
        @change="loadSelectedCriterion"
      >
        <b-form-select-option :value="null" disabled>
          請選擇準則庫
        </b-form-select-option>
        <b-form-select-option
          v-for="entry in criteria"
          :key="entry.id"
          :value="entry.id"
        >
          {{ entry.name }}
        </b-form-select-option>
      </b-form-select>
    </b-form-group>

    <b-alert :show="Boolean(criterionError)" variant="danger">
      {{ criterionError }}
    </b-alert>

    <div v-if="criterion.id && !isLoadingCriterion">
      <p>{{ criterion.name }}</p>

      <b-form-group
        label="載入既有人工結果（討論後修訂用）"
        label-for="qa-human-source"
      >
        <b-input-group>
          <b-form-select
            id="qa-human-source"
            v-model="selectedSourceId"
            :disabled="isBusyWithHumanResults"
          >
            <b-form-select-option :value="null" disabled>
              請選擇要修訂的人工結果
            </b-form-select-option>
            <b-form-select-option
              v-for="record in humanResults"
              :key="record.id"
              :value="record.id"
            >
              {{ record.name }}（ID：{{ record.id }}）
            </b-form-select-option>
          </b-form-select>
          <b-input-group-append>
            <b-button
              variant="outline-primary"
              :disabled="!selectedSourceId || isBusyWithHumanResults"
              @click="loadExistingResult"
            >
              <b-icon icon="box-arrow-in-down" aria-hidden="true" />
              載入
            </b-button>
            <b-button
              :disabled="isBusyWithHumanResults"
              title="更新人工結果列表"
              aria-label="更新人工結果列表"
              @click="loadHumanResults"
            >
              <b-icon icon="arrow-clockwise" aria-hidden="true" />
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <small class="text-muted d-block mt-1">
          僅限載入自己的結果。載入修改後儲存會覆蓋原紀錄；已被校準或應用庫引用的紀錄無法覆蓋。
        </small>
      </b-form-group>
      <b-alert :show="Boolean(humanResultsError)" variant="danger">
        {{ humanResultsError }}
      </b-alert>
      <p v-if="sourceResult">
        目前修訂：{{ sourceResult.name }}（ID：{{ sourceResult.id }}），儲存時會覆蓋這筆紀錄。
      </p>

      <b-form-group label="編碼員名稱" label-for="qa-coder-name">
        <b-form-input
          id="qa-coder-name"
          v-model.trim="coderName"
          :disabled="isImporting || isSavingResult"
          maxlength="128"
        />
      </b-form-group>

      <b-form-group label="匯入人工編碼結果" label-for="qa-human-result-file">
        <b-form-file
          id="qa-human-result-file"
          v-model="humanResultFile"
          accept=".xlsx,.xls,.csv"
          placeholder="選擇結果檔案"
          drop-placeholder="放開以選取檔案"
          browse-text="選擇檔案"
          :disabled="isImporting || isSavingResult"
          @input="importHumanResults"
        />
      </b-form-group>

      <p v-if="isImporting">正在匯入，請完成確認操作。</p>

      <b-alert
        :show="Boolean(humanImportError)"
        variant="danger"
        style="white-space: pre-line"
      >
        {{ humanImportError }}
      </b-alert>
      <b-alert :show="Boolean(humanImportSuccess)" variant="success">
        {{ humanImportSuccess }}
      </b-alert>

      <p v-if="humanResultFile">已選擇：{{ humanResultFile.name }}</p>

      <b-alert :show="Boolean(draftError)" variant="danger">
        {{ draftError }}
      </b-alert>

      <p>已完成 {{ completedCount }} / {{ criterion.items.length }} 題</p>

      <b-progress
        :value="completedCount"
        :max="criterion.items.length"
        class="mb-3"
      />

      <b-button
        variant="outline-danger"
        class="mb-3"
        :disabled="isImporting || isSavingResult"
        @click="clearDraft"
      >
        <b-icon icon="trash" aria-hidden="true" />
        清除草稿
      </b-button>

      <div
        v-if="
          criterion.codingType === 'scale' || criterion.codingType === 'score'
        "
        class="mb-3"
      >
        <p>
          {{ criterion.scale.min }} 分：
          {{ criterion.scale.minDef }}
        </p>
        <p>
          {{ criterion.scale.max }} 分：
          {{ criterion.scale.maxDef }}
        </p>
        <p
          v-for="(definition, point) in criterion.scale.pointDefs"
          :key="point"
        >
          {{ point }} 分：{{ definition }}
        </p>
      </div>

      <div
        v-for="item in criterion.items"
        :key="item.no"
        class="mb-4"
        tabindex="0"
        @focusin="activeItemNo = item.no"
        @keydown="handleLabelKey($event, item.no)"
      >
        <p>{{ item.no }}. {{ item.text }}</p>
        <p v-if="activeItemNo === item.no" class="text-primary">目前題目</p>

        <div v-if="criterion.codingType === 'label'">
          <b-button
            v-for="label in criterion.labels"
            :key="label.code"
            :variant="
              codes[String(item.no)] === label.code
                ? 'primary'
                : 'outline-secondary'
            "
            class="mr-2 mb-2"
            :title="label.description"
            :disabled="isImporting || isSavingResult"
            @click="selectCode(item.no, label.code)"
          >
            {{ label.code }}：{{ label.description }}
          </b-button>
        </div>
        <div v-else-if="criterion.codingType === 'scale'">
          <b-button
            v-for="point in scalePoints"
            :key="point"
            :variant="
              codes[String(item.no)] === point ? 'primary' : 'outline-secondary'
            "
            :aria-pressed="codes[String(item.no)] === point"
            class="mr-2 mb-2"
            :disabled="isImporting || isSavingResult"
            @click="selectCode(item.no, point)"
          >
            {{ point }}
          </b-button>
        </div>
        <div v-else-if="criterion.codingType === 'score'">
          <b-form-group label="分數" :label-for="'qa-score-' + item.no">
            <b-form-input
              :id="'qa-score-' + item.no"
              type="number"
              step="any"
              :min="criterion.scale.min"
              :max="criterion.scale.max"
              :value="codes[String(item.no)]"
              :disabled="isImporting || isSavingResult"
              @input="setScore(item.no, $event)"
            />
          </b-form-group>
          <p>有效範圍：{{ criterion.scale.min }}–{{ criterion.scale.max }}</p>
          <b-alert
            :show="
              codes[String(item.no)] !== undefined &&
              codes[String(item.no)] !== '' &&
              !isValidCode(codes[String(item.no)])
            "
            variant="danger"
          >
            請輸入範圍內的有效分數。
          </b-alert>
        </div>
      </div>

      <b-alert :show="canSave" variant="success">
        全部題目已完成，且已填寫編碼員名稱。
      </b-alert>

      <b-button
        variant="primary"
        :disabled="
          !canSave || isSavingResult || isImporting || isLoadingCriterion
        "
        @click="saveHumanResult"
      >
        <b-icon icon="check-circle" aria-hidden="true" />
        {{
          isSavingResult
            ? "儲存中…"
            : sourceResultId
              ? "覆蓋原紀錄"
              : "儲存人工結果"
        }}
      </b-button>

      <b-alert :show="Boolean(saveResultError)" variant="danger" class="mt-3">
        {{ saveResultError }}
      </b-alert>

      <b-alert
        :show="Boolean(saveResultSuccess)"
        variant="success"
        class="mt-3"
      >
        {{ saveResultSuccess }}
      </b-alert>
    </div>
  </section>
</template>

<script>
import axios from "axios";
import { parseHumanResultFile } from "@/utils/qaExcel";
export default {
  name: "QACoder",

  data() {
    return {
      criterion: {
        id: null,
        name: "",
        codingType: "",
        scale: null,
        labels: [],
        items: [],
      },
      criteria: [],
      selectedCriterionId: null,
      isLoadingCriterion: false,
      criterionError: "",
      codes: {},
      coderName: "",
      draftError: "",
      activeItemNo: null,
      humanResultFile: null,
      humanImportError: "",
      isImporting: false,
      humanImportSuccess: "",
      isSavingResult: false,
      saveResultError: "",
      saveResultSuccess: "",
      humanResults: [],
      isLoadingHumanResults: false,
      humanResultsError: "",
      selectedSourceId: null,
      sourceResultId: null,
    };
  },

  methods: {
    async saveHumanResult() {
      if (
        !this.canSave ||
        this.isSavingResult ||
        this.isImporting ||
        this.isLoadingCriterion
      ) {
        return;
      }

      this.isSavingResult = true;
      this.saveResultError = "";
      this.saveResultSuccess = "";

      try {
        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");
        const url = `${base.replace(/\/+$/, "")}/api/qa/results`;

        if (this.sourceResultId) {
          const confirmed = await this.$bvModal.msgBoxConfirm(
            `確定以目前的編碼覆蓋結果 ID：${this.sourceResultId}？覆蓋後無法復原。`,
            {
              title: "覆蓋原紀錄",
              okTitle: "覆蓋",
              cancelTitle: "取消",
              okVariant: "danger",
              centered: true,
            },
          );
          if (!confirmed) return;

          await axios.patch(
            url,
            { name: this.coderName.trim(), codes: { ...this.codes } },
            { params: { id: this.sourceResultId } },
          );
          this.saveResultSuccess = `已覆蓋結果 ID：${this.sourceResultId}`;
        } else {
          const response = await axios.post(url, {
            criterionId: this.criterion.id,
            name: this.coderName.trim(),
            isAI: false,
            content: {
              codes: { ...this.codes },
              source: "manual",
            },
            note: null,
          });
          this.saveResultSuccess = `人工結果已儲存，結果 ID：${response.data.id}`;
        }
        this.loadHumanResults();
      } catch (error) {
        this.saveResultError =
          error.response?.data?.message || error.message || "儲存人工結果失敗";
      } finally {
        this.isSavingResult = false;
      }
    },

    async loadHumanResults() {
      if (!this.criterion.id || this.isLoadingHumanResults) return;

      this.isLoadingHumanResults = true;
      this.humanResultsError = "";

      try {
        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");

        const response = await axios.get(
          `${base.replace(/\/+$/, "")}/api/qa/results`,
          { params: { criterionId: this.criterion.id, includeCodes: "1" } },
        );

        this.humanResults = response.data.filter(
          (record) => record.isAI === false && record.codes,
        );
        const ids = this.humanResults.map((record) => record.id);
        if (!ids.includes(this.selectedSourceId)) this.selectedSourceId = null;
      } catch (error) {
        this.humanResultsError =
          error.response?.data?.message ||
          error.message ||
          "讀取人工結果列表失敗";
      } finally {
        this.isLoadingHumanResults = false;
      }
    },

    async loadExistingResult() {
      const record = this.humanResults.find(
        (entry) => entry.id === this.selectedSourceId,
      );
      if (!record || this.isBusyWithHumanResults) return;

      if (Object.keys(this.codes).length || this.coderName) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          `載入「${record.name}」會取代目前的編碼員名稱與所有編碼，是否繼續？`,
          {
            title: "載入既有結果",
            okTitle: "載入",
            cancelTitle: "取消",
            centered: true,
          },
        );
        if (!confirmed) return;
      }

      const codes = {};
      this.criterion.items.forEach((item) => {
        const key = String(item.no);
        if (this.isValidCode(record.codes[key])) codes[key] = record.codes[key];
      });

      this.codes = codes;
      this.coderName = record.name;
      this.sourceResultId = record.id;
      this.humanResultFile = null;
      this.humanImportError = "";
      this.humanImportSuccess = "";
      this.saveResultError = "";
      this.saveResultSuccess = "";
    },

    selectCode(itemNo, code) {
      if (this.isImporting || this.isSavingResult) return;
      this.$set(this.codes, String(itemNo), code);
    },

    isValidCode(code) {
      if (this.criterion.codingType === "label") {
        return this.criterion.labels.some((label) => label.code === code);
      }

      if (
        this.criterion.codingType !== "scale" &&
        this.criterion.codingType !== "score"
      ) {
        return false;
      }

      const range = this.criterion.scale;

      if (!range || !Number.isFinite(code)) {
        return false;
      }

      if (code < range.min || code > range.max) {
        return false;
      }

      return this.criterion.codingType === "score" || Number.isInteger(code);
    },

    setScore(itemNo, value) {
      if (this.isImporting || this.isSavingResult) return;
      const text = String(value).trim();
      const code = text === "" ? "" : Number(text);

      this.$set(this.codes, String(itemNo), code);
    },

    saveDraft() {
      if (!this.criterion.id || this.isLoadingCriterion) return;

      try {
        localStorage.setItem(
          this.draftKey,
          JSON.stringify({
            coderName: this.coderName,
            codes: this.codes,
            sourceResultId: this.sourceResultId,
          }),
        );
      } catch {
        this.draftError = "無法保存草稿，請確認瀏覽器儲存空間與設定";
      }
    },

    restoreDraft() {
      if (!this.criterion.id) return;

      try {
        const saved = localStorage.getItem(this.draftKey);
        if (!saved) return;

        const draft = JSON.parse(saved);

        if (
          !draft ||
          typeof draft !== "object" ||
          typeof draft.coderName !== "string" ||
          !draft.codes ||
          typeof draft.codes !== "object" ||
          Array.isArray(draft.codes)
        ) {
          throw new Error("草稿格式錯誤");
        }

        const restoredCodes = {};

        this.criterion.items.forEach((item) => {
          const key = String(item.no);
          const code = draft.codes[key];

          if (this.isValidCode(code)) {
            restoredCodes[key] = code;
          }
        });

        this.coderName = draft.coderName;
        this.codes = restoredCodes;
        this.sourceResultId = Number.isSafeInteger(draft.sourceResultId)
          ? draft.sourceResultId
          : null;
      } catch {
        this.draftError = "無法恢復草稿，請重新編碼";
      }
    },

    async clearDraft() {
      if (this.isImporting || this.isSavingResult) return;

      const confirmed = await this.$bvModal.msgBoxConfirm(
        "要清除目前的編碼員名稱與所有編碼結果嗎？",
        {
          title: "清除草稿",
          okTitle: "清除",
          cancelTitle: "取消",
          okVariant: "danger",
          centered: true,
        },
      );

      if (!confirmed) return;

      this.coderName = "";
      this.codes = {};
      this.sourceResultId = null;
      this.selectedSourceId = null;
      this.draftError = "";
      this.humanResultFile = null;
      this.humanImportError = "";
      this.humanImportSuccess = "";

      await this.$nextTick();

      try {
        localStorage.removeItem(this.draftKey);
      } catch {
        this.draftError = "畫面已清空，但無法刪除瀏覽器中的草稿";
      }
    },
    handleLabelKey(event, itemNo) {
      if (this.isImporting || this.isSavingResult) return;

      if (
        this.criterion.codingType !== "label" ||
        event.ctrlKey ||
        event.altKey ||
        event.metaKey ||
        event.isComposing
      ) {
        return;
      }

      const target = event.target;

      if (
        target instanceof HTMLElement &&
        (target.matches("input, textarea, select") || target.isContentEditable)
      ) {
        return;
      }

      const code = event.key.toUpperCase();

      if (!this.isValidCode(code)) return;

      event.preventDefault();
      this.activeItemNo = itemNo;
      this.selectCode(itemNo, code);
    },
    async importHumanResults(file) {
      if (!file || this.isImporting || this.isSavingResult) return;
      this.humanImportError = "";
      this.humanImportSuccess = "";

      this.isImporting = true;

      try {
        const entries = await parseHumanResultFile(file);
        const codes = {};
        const seen = new Set();
        const errors = [];
        const expected = new Set(
          this.criterion.items.map((item) => String(item.no)),
        );

        entries.forEach(({ rowNumber, no, value }) => {
          if (!expected.has(no)) {
            errors.push(`第 ${rowNumber} 列：題號「${no}」不在準則中`);
          }
          if (seen.has(no)) {
            errors.push(`第 ${rowNumber} 列：題號「${no}」重複`);
          }
          seen.add(no);

          const code =
            this.criterion.codingType === "label"
              ? value
              : value === ""
              ? ""
              : Number(value);

          if (!this.isValidCode(code)) {
            errors.push(`第 ${rowNumber} 列：結果「${value}」不合法`);
          }
          codes[no] = code;
        });

        expected.forEach((no) => {
          if (!seen.has(no)) errors.push(`缺少第 ${no} 題的結果`);
        });

        if (errors.length) throw new Error(errors.join("\n"));

        if (Object.keys(this.codes).length > 0) {
          const confirmed = await this.$bvModal.msgBoxConfirm(
            "匯入會取代目前所有題目的編碼結果，是否繼續？",
            {
              title: "匯入人工結果",
              okTitle: "取代結果",
              cancelTitle: "取消",
              centered: true,
            },
          );

          if (!confirmed) return;
        }

        this.codes = codes;
        this.humanImportSuccess = `已匯入 ${
          Object.keys(codes).length
        } 題人工編碼結果，尚未存入結果庫。`;
      } catch (error) {
        this.humanImportError = error.message || "無法匯入結果";
      } finally {
        this.isImporting = false;
        this.humanResultFile = null;
      }
    },
    async loadCriteria() {
      if (this.isLoadingCriterion || this.isImporting || this.isSavingResult)
        return;

      this.isLoadingCriterion = true;
      this.criterionError = "";

      try {
        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");

        const response = await axios.get(
          `${base.replace(/\/+$/, "")}/api/qa/criteria`,
        );
        this.criteria = response.data;
      } catch (error) {
        this.criterionError =
          error.response?.data?.message || error.message || "讀取失敗";
      } finally {
        this.isLoadingCriterion = false;
      }
    },

    async loadSelectedCriterion(id) {
      if (
        !id ||
        this.isLoadingCriterion ||
        this.isImporting ||
        this.isSavingResult
      )
        return;

      this.isLoadingCriterion = true;
      this.criterionError = "";

      try {
        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");

        const response = await axios.get(
          `${base.replace(/\/+$/, "")}/api/qa/criteria/${id}`,
        );

        this.criterion = response.data;
        this.codes = {};
        this.coderName = "";
        this.humanResults = [];
        this.selectedSourceId = null;
        this.sourceResultId = null;
        this.activeItemNo = null;
        this.draftError = "";
        this.humanResultFile = null;
        this.humanImportError = "";
        this.humanImportSuccess = "";
        this.saveResultError = "";
        this.saveResultSuccess = "";

        await this.$nextTick();
        this.restoreDraft();
        this.loadHumanResults();
      } catch (error) {
        this.selectedCriterionId = this.criterion.id;
        this.criterionError =
          error.response?.data?.message || error.message || "讀取失敗";
      } finally {
        await this.$nextTick();
        this.isLoadingCriterion = false;
      }
    },
  },

  computed: {
    completedCount() {
      return this.criterion.items.filter((item) =>
        this.isValidCode(this.codes[String(item.no)]),
      ).length;
    },

    canSave() {
      return (
        this.criterion.items.length > 0 &&
        this.completedCount === this.criterion.items.length &&
        this.coderName.trim().length > 0
      );
    },

    isBusyWithHumanResults() {
      return (
        this.isLoadingHumanResults ||
        this.isImporting ||
        this.isSavingResult ||
        this.isLoadingCriterion
      );
    },

    sourceResult() {
      return (
        this.humanResults.find((record) => record.id === this.sourceResultId) ||
        null
      );
    },

    draftKey() {
      return `qa-human-draft:${this.criterion.id}`;
    },

    scalePoints() {
      const range = this.criterion.scale;

      if (
        this.criterion.codingType !== "scale" ||
        !range ||
        !Number.isInteger(range.min) ||
        !Number.isInteger(range.max) ||
        range.min >= range.max
      ) {
        return [];
      }

      const points = [];

      for (let point = range.min; point <= range.max; point += 1) {
        points.push(point);
      }

      return points;
    },
  },

  watch: {
    coderName() {
      this.saveDraft();
    },

    codes: {
      deep: true,
      handler() {
        this.saveDraft();
      },
    },
  },

  mounted() {
    this.loadCriteria();
  },
};
</script>
